import { QueryClient, QueryFunction } from "@tanstack/react-query";

// ─── CSRF token cache ────────────────────────────────────────────────────────
// The server sets a signed CSRF cookie and returns the matching token value
// from GET /api/csrf-token. We cache the token in memory for the lifetime of
// the page and send it as "x-csrf-token" on every state-changing request.
// If the server returns 403 with code INVALID_CSRF_TOKEN we clear the cache
// and retry once (handles session expiry / page-reload edge cases).

let _csrfToken: string | null = null;

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch("/api/csrf-token", { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to fetch CSRF token: ${res.status}`);
  const data = await res.json();
  return data.csrfToken as string;
}

async function getCsrfToken(): Promise<string> {
  if (!_csrfToken) {
    _csrfToken = await fetchCsrfToken();
  }
  return _csrfToken;
}

export function clearCsrfToken(): void {
  _csrfToken = null;
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

const STATE_CHANGING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// ─── apiRequest ──────────────────────────────────────────────────────────────
// Used by mutations throughout the app. Automatically attaches the CSRF token
// for state-changing methods and retries once if the token is rejected.

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const upperMethod = method.toUpperCase();
  const isStateful = STATE_CHANGING_METHODS.has(upperMethod);

  const buildHeaders = async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {};
    if (data) headers["Content-Type"] = "application/json";
    if (isStateful) headers["x-csrf-token"] = await getCsrfToken();
    return headers;
  };

  const makeRequest = async (): Promise<Response> =>
    fetch(url, {
      method,
      headers: await buildHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

  let res = await makeRequest();

  // Retry once on CSRF rejection (e.g. after session expiry or tab restore)
  if (res.status === 403 && isStateful) {
    const body = await res.clone().json().catch(() => ({}));
    if (body?.code === "INVALID_CSRF_TOKEN") {
      clearCsrfToken();
      res = await makeRequest();
    }
  }

  await throwIfResNotOk(res);
  return res;
}

// ─── getQueryFn ──────────────────────────────────────────────────────────────
// Used by React Query for GET (read-only) requests. No CSRF token needed.

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
